import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthBllService } from '../auth-bll.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/components/loader/loader.service';
import { defaultRouteAfterLogin } from '../auth.model';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [CommonModule, LoaderComponent],
  templateUrl: './callback.component.html',
  styleUrls: ['./callback.component.scss'],
})
export class CallbackComponent implements OnInit {
  /** Indicates whether the authentication exchange is in progress. */
  // isLoading = true;
  /** Holds any error message to display if login fails or parameters are missing. */
  error: string | null = null;
  private loaderService = inject(LoaderService);

  /**
   * @param route        Provides access to query parameters returned by the OAuth server.
   * @param authService  Business-logic service that completes the PKCE code exchange.
   * @param router       Angular Router used to navigate on success or further errors.
   */
  constructor(
    private route: ActivatedRoute,
    private authService: AuthBllService,
    private router: Router
  ) { }

  /**
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   *
   * Handles the OAuth callback by extracting authorization parameters from the query string,
   * validating them, and completing the authentication process. On successful authentication,
   * redirects the user to the appropriate route based on the stored state. Handles errors by
   * displaying an error message and always hides the loading indicator when done.
   *
   * @remarks
   * - Expects `code` and `auth_progress_id` parameters in the query string.
   * - Uses `authService` to complete authentication and manage state.
   * - Uses `loaderService` to show/hide a loading indicator during the process.
   * - Navigates to `/home` if the state is 'virtual-event', otherwise navigates to the route specified by `state`.
   */
  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const code = params['code'];
      const authKey = params['auth_progress_id'];
      let state = params['state'] || defaultRouteAfterLogin; 
      const refSource = params['ref_source'] || null;
      const userId = params["userId"] as string | undefined;

      if (state?.startsWith("/catalogue/company-details")){
        if (userId){
          // state = state + `&userId=${userId}`;
            // Safely append userId as a query parameter
            try {
              // Use window.location.origin as base for relative URLs
              const url = new URL(state, window.location.origin);
              url.searchParams.set('userId', userId);
              // Remove the origin to get the relative path with query
              state = url.pathname + url.search + url.hash;
            } catch (e) {
              // Fallback: append with correct separator
              if (state.includes('?')) {
                state = state + `&userId=${userId}`;
              } else {
                state = state + `?userId=${userId}`;
              }
            }
        }
      }
      if (!code || !authKey) {
        // Missing required OAuth parameters
        this.error = 'Missing authorization parameters.';
        return;
      }

      this.loaderService.show();
      this.authService
        .completeAuthenticate(authKey, code, refSource ?? undefined)
        .then(() => {
          // On success, redirect to the home page
          if (state === 'virtual-event') {
            this.router.navigate(['/home'], { replaceUrl: true });
            return;
          }
          this.router.navigateByUrl(state, { replaceUrl: true })
        }
        )
        .catch((err) => {
          // Log and display any error during token exchange
          console.error(err);
          this.error = err.message || 'Login failed';
        })
        .finally(() => {
          // Always stop the loading indicator
          this.loaderService.hide();
        });
    });
  }
}
